import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CustomField,
  IEmailTrigger,
  MailrailAutoSendEmailCampaign,
  TComparison,
} from '@feathr/blackbox';
import { FieldCollection, Flavors } from '@feathr/blackbox';
import { Value } from '@feathr/components';
import type { IAttrOption } from '@feathr/extender/components/EditFilters/PredicateInputs';
import { useStore } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

import * as styles from './MailrailTriggerSummary.css';

import defaultBreadcrumbFields from '@feathr/blackbox/fixtures/defaultBreadcrumbFields.json';
import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

interface IMailrailTriggerSummaryProps {
  /** A Mailrail autosend/drip campaign — reads its `predicates` enrollment trigger. */
  campaign: MailrailAutoSendEmailCampaign;
}

interface IComparisonOption {
  id: TComparison;
  name: string;
}

function getComparisons(t: TFunction, attr: IEmailTrigger): IComparisonOption[] {
  const baseOptions: IComparisonOption[] = [
    { id: 'exists', name: t('exists') },
    { id: 'nexists', name: t('does not exist') },
  ];
  const numberOptions: IComparisonOption[] = [
    ...baseOptions,
    { id: 'eq', name: t('equals') },
    { id: 'ne', name: t('does not equal') },
    { id: 'gt', name: t('is greater than') },
    { id: 'lt', name: t('is less than') },
  ];
  switch (attr.attr_type) {
    case 'string':
      if (attr.attr_against?.includes('_id')) {
        return [...baseOptions, { id: 'eq', name: t('is') }, { id: 'ne', name: t('is not') }];
      }
      return [
        ...baseOptions,
        { id: 'eq', name: t('is') },
        { id: 'ne', name: t('is not') },
        { id: 'starts_with', name: t('starts with') },
        { id: 'wildcard', name: t('matches pattern') },
        { id: 'regexp', name: t('matches the expression') },
        { id: 'contains_substring', name: t('contains') },
      ];

    case 'integer':

    case 'float':
      return numberOptions;

    case 'boolean':
      return [
        { id: 'eq', name: t('is') },
        { id: 'ne', name: t('is not') },
      ];

    case 'date':
      return [
        ...baseOptions,
        { id: 'eq_date_r', name: t('is') },
        { id: 'ne_date_r', name: t('is not') },
        { id: 'gt_date_r', name: t('is after') },
        { id: 'lt_date_r', name: t('is before') },
      ];

    case 'list':
      return [
        ...baseOptions,
        { id: 'in', name: t('contains') },
        { id: 'nin', name: t('does not contain') },
      ];

    default:
      return baseOptions;
  }
}

function customFieldToOption(field: CustomField): IAttrOption {
  return {
    id: `custom.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
    kind: field.get('collection') as 'Person' | 'Breadcrumb',
    helpText: field.get('description'),
  };
}

/**
 * Read-only summary of a Mailrail drip/autosend campaign's enrollment trigger. Renders one sentence
 * per predicate (field name + comparison + value), or `(None)` when there are no predicates. Renders
 * ONLY the predicate trigger list — trigger type and delay are surfaced separately by the preview.
 *
 * @param props - {@link IMailrailTriggerSummaryProps}.
 */
const MailrailTriggerSummary = observer(
  ({ campaign }: IMailrailTriggerSummaryProps): JSX.Element => {
    const { t } = useTranslation();
    const { CustomFields, Campaigns, Forms, Events, Partners, Segments, Tags } = useStore();
    const customFields = CustomFields.list({
      filters: { collection__in: [FieldCollection.Person, FieldCollection.Breadcrumb] },
      pagination: { page: 0, page_size: 1000 },
    });

    const idValueToCollection = {
      cpn_id: Campaigns,
      form_id: Forms,
      e_id: Events,
      p_id: Partners,
      seg_id: Segments,
      tag_id: Tags,
      tag_ids: Tags,
    };

    const allFields = [
      ...defaultPersonFields,
      ...defaultBreadcrumbFields,
      ...customFields.models.map((field) => customFieldToOption(field)),
    ];

    const predicates = campaign.get('predicates') ?? [];

    if (predicates.length === 0) {
      return <>{t('(None)')}</>;
    }

    return (
      <ul className={styles.triggerList}>
        {predicates.map((trigger, i) => {
          const attrLabel =
            allFields.find((field) => field.id === trigger.attr_against)?.name ?? '-';
          const comparison =
            getComparisons(t, trigger).find((option) => option.id === trigger.comparison)?.name ??
            '-';

          function getValueLabel(
            value?: IEmailTrigger['value'],
            comparison?: string,
          ): JSX.Element | string | number | null {
            if (trigger.comparison && ['exists', 'nexists'].includes(trigger.comparison)) {
              return null;
            }

            if (value === undefined) {
              return <em>{t('empty')}</em>;
            }

            if (comparison?.includes('_r') && typeof value === 'number') {
              if (value === 0) {
                return t('today');
              }

              if (value >= 1) {
                return t('{{count}} day from now', { count: value });
              }

              if (value < 0) {
                return t('{{count}} day ago', { count: Math.abs(value) });
              }
            }

            if (trigger.attr_type === 'boolean') {
              return value ? t('True') : t('False');
            }

            if (trigger.attr_type === 'date') {
              return moment(value as string | number).format(TimeFormat.shortWeekdayDateTime);
            }

            if (trigger.attr_against === 'flvr') {
              return Flavors[trigger.value as keyof typeof Flavors];
            }

            if (
              trigger.attr_against &&
              Object.keys(idValueToCollection).includes(trigger.attr_against)
            ) {
              const collection =
                idValueToCollection[trigger.attr_against as keyof typeof idValueToCollection];
              const val = trigger.value as string | string[];
              return Array.isArray(val)
                ? val.map((entry) => collection.get(entry).name).join(', ')
                : collection.get(val)?.name;
            }

            if (trigger.attr_type === 'list' && Array.isArray(value)) {
              return value.join(', ');
            }

            return value as string | number;
          }

          const value =
            attrLabel === '-' && comparison === '-' && trigger.value === undefined ? (
              t('Not set')
            ) : (
              <>
                {attrLabel} {comparison} {getValueLabel(trigger.value, trigger.comparison)}
              </>
            );

          return (
            // index is ok as a key here because we aren't rearranging these elements
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>
              <Value className={styles.triggerActivity} value={value} />
            </li>
          );
        })}
      </ul>
    );
  },
);

MailrailTriggerSummary.displayName = 'MailrailTriggerSummary';

export default MailrailTriggerSummary;
